import { useState } from "react";
import { Tent, Mail, MapPin, ArrowRight, Check } from "lucide-react";
import { Container } from "@/components/ui/container";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface FooterProps {
  onNavigate: (path: string) => void;
}

export function Footer({ onNavigate }: FooterProps) {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState<"idle" | "ok" | "error">("idle");

  const kategorien = [
    { href: "/kuppelzelte", label: "Kuppelzelte" },
    { href: "/tunnelzelte", label: "Tunnelzelte" },
    { href: "/familienzelte", label: "Familienzelte" },
    { href: "/trekkingzelte", label: "Trekkingzelte" },
    { href: "/geodaet-zelte", label: "Geodät-Zelte" },
    { href: "/dachzelte", label: "Dachzelte" },
    { href: "/wurfzelte", label: "Wurfzelte" },
    { href: "/tipi-zelte", label: "Tipi-Zelte" },
  ];

  const mehrKategorien = [
    { href: "/winterzelte", label: "Winterzelte" },
    { href: "/festivalzelte", label: "Festivalzelte" },
    { href: "/campingzelte", label: "Campingzelte" },
    { href: "/leichte-zelte", label: "Leichte Zelte" },
    { href: "/2-personen-zelte", label: "2-Personen-Zelte" },
    { href: "/3-personen-zelte", label: "3-Personen-Zelte" },
    { href: "/4-personen-zelte", label: "4-Personen-Zelte" },
    { href: "/6-personen-zelte", label: "6-Personen-Zelte" },
    { href: "/glamping-zelte", label: "Glamping Zelte" },
    { href: "/aufblasbare-zelte", label: "Aufblasbare Zelte" },
    { href: "/grosse-zelte", label: "Große Zelte" },
    { href: "/guenstige-zelte", label: "Günstige Zelte" },
    { href: "/partyzelte", label: "Partyzelte" },
  ];

  const links = [
    { href: "/", label: "Startseite" },
    { href: "/zelte", label: "Alle Zelte" },
    { href: "/vergleich", label: "Zelt-Vergleich" },
    { href: "/finder", label: "Zelt-Finder" },
    { href: "/ratgeber", label: "Ratgeber" },
  ];

  const partnerLinks = [
    { href: "https://hellcase-promo-codes.com/", label: "Hellcase Promo Codes" },
    { href: "https://cs2gamblingsites.com/", label: "CS2 Gambling Sites" },
    { href: "https://diamantenkaufen.org/", label: "Diamanten Kaufen" },
    { href: "https://onlinegeldverdienen1.com/", label: "Online Geld Verdienen" },
    { href: "https://vr-boxing.com/", label: "VR Boxing" },
    { href: "https://bestvr.site/", label: "Best VR" },
  ];

  return (
    <footer className="relative border-t bg-muted/30 overflow-hidden">
      <div className="absolute inset-0 mesh-gradient opacity-30" />

      <Container className="relative">
        {/* Newsletter Section */}
        <div className="py-12 border-b border-border/50">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-3">
              Bleiben Sie auf dem Laufenden
            </h3>
            <p className="text-muted-foreground mb-6">
              Erhalten Sie die neuesten Zelt-Bewertungen, Tipps und Angebote direkt in Ihr Postfach.
            </p>
            {newsletterStatus === "ok" ? (
              <p className="inline-flex items-center gap-2 text-sm font-medium text-primary">
                <Check className="h-4 w-4" />
                Danke! Wir merken uns Ihre Adresse lokal – es geht noch kein Newsletter raus.
              </p>
            ) : (
              <form
                className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                onSubmit={e => {
                  e.preventDefault();
                  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
                  if (!valid) {
                    setNewsletterStatus("error");
                    return;
                  }
                  try {
                    localStorage.setItem("zelt-newsletter-email", email.trim());
                  } catch {
                    // ignore
                  }
                  setNewsletterStatus("ok");
                  setEmail("");
                }}
              >
                <label htmlFor="newsletter-email" className="sr-only">E-Mail-Adresse</label>
                <input
                  id="newsletter-email"
                  type="email"
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value);
                    if (newsletterStatus === "error") setNewsletterStatus("idle");
                  }}
                  placeholder="Ihre E-Mail-Adresse"
                  autoComplete="email"
                  required
                  className={cn(
                    "flex-1 h-12 px-4 rounded-xl",
                    "bg-background border border-border",
                    "text-sm placeholder:text-muted-foreground",
                    "focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50",
                    "transition-all duration-300",
                    newsletterStatus === "error" && "border-destructive"
                  )}
                />
                <Button type="submit" className="h-12 px-6 rounded-xl btn-premium text-primary-foreground">
                  <span className="relative z-10 flex items-center">
                    Abonnieren
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </span>
                </Button>
              </form>
            )}
            {newsletterStatus === "error" && (
              <p className="text-sm text-destructive mt-3">Bitte geben Sie eine gültige E-Mail-Adresse ein.</p>
            )}
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="py-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2 space-y-5">
            <a
              href="/"
              onClick={e => { e.preventDefault(); onNavigate("/"); }}
              className="group inline-flex items-center gap-2.5"
            >
              <div className={cn(
                "w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300",
                "bg-gradient-to-br from-primary to-primary/80",
                "group-hover:shadow-lg group-hover:shadow-primary/25 group-hover:scale-105"
              )}>
                <Tent className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="font-bold text-xl">zelt.kaufen</span>
            </a>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Ihr unabhängiger Ratgeber für Zelte und Campingausrüstung. Vergleichen Sie die besten Zelte und finden Sie das perfekte Modell für Ihre Abenteuer.
            </p>
            <a
              href="mailto:mail@pykez.com"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <Mail className="w-4 h-4" />
              mail@pykez.com
            </a>
          </div>

          {/* Zelttypen */}
          <div>
            <h4 className="font-semibold mb-4">Zelttypen</h4>
            <ul className="space-y-2.5">
              {kategorien.map(item => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    onClick={e => { e.preventDefault(); onNavigate(item.href); }}
                    className="group text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                  >
                    <span className="w-0 group-hover:w-2 h-px bg-primary transition-all duration-300" />
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Mehr Kategorien */}
          <div>
            <h4 className="font-semibold mb-4">Mehr Kategorien</h4>
            <ul className="space-y-2.5">
              {mehrKategorien.map(item => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    onClick={e => { e.preventDefault(); onNavigate(item.href); }}
                    className="group text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                  >
                    <span className="w-0 group-hover:w-2 h-px bg-primary transition-all duration-300" />
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Navigation + Kontakt */}
          <div>
            <h4 className="font-semibold mb-4">Navigation</h4>
            <ul className="space-y-2.5">
              {links.map(item => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    onClick={e => { e.preventDefault(); onNavigate(item.href); }}
                    className="group text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                  >
                    <span className="w-0 group-hover:w-2 h-px bg-primary transition-all duration-300" />
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-6 space-y-3">
              <a
                href="mailto:mail@pykez.com"
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Mail className="h-3.5 w-3.5" />
                mail@pykez.com
              </a>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" />
                Bad Hersfeld, DE
              </div>
            </div>
          </div>

          {/* Partner */}
          <div>
            <h4 className="font-semibold mb-4">Partner</h4>
            <ul className="space-y-2.5">
              {partnerLinks.map(item => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                  >
                    <span className="w-0 group-hover:w-2 h-px bg-primary transition-all duration-300" />
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-border/50 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            &copy; {currentYear} zelt.kaufen. Alle Rechte vorbehalten
          </p>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <a
              href="/datenschutz"
              onClick={e => { e.preventDefault(); onNavigate("/datenschutz"); }}
              className="hover:text-foreground transition-colors"
            >
              Datenschutz
            </a>
            <a
              href="/impressum"
              onClick={e => { e.preventDefault(); onNavigate("/impressum"); }}
              className="hover:text-foreground transition-colors"
            >
              Impressum
            </a>
            <a
              href="/agb"
              onClick={e => { e.preventDefault(); onNavigate("/agb"); }}
              className="hover:text-foreground transition-colors"
            >
              AGB
            </a>
          </div>
        </div>
      </Container>
    </footer>
  );
}
